import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Col, Form, Input, Row, Modal } from "antd";
import Icon, { WarningOutlined } from "@ant-design/icons";
import Text from "antd/es/typography/Text";
import {
  addPlusToNumber,
  emailPattern,
  emailValid,
  maskEmail,
  phoneValid,
} from "../../../utils";
import { _login } from "../../../store/actions/userAction";
import { DoneIcon } from "../../../assets/icons/DoneIcon";
import { CoolIcon } from "../../../assets/icons/CoolIcon";
import { useAppDispatch, useAppSelector } from "../../../store";
import styles from "./Anytime.module.css";

const Anytime: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { _load_button, _errors } = useAppSelector(
    ({ userSlice }) => userSlice
  );

  const [form] = Form.useForm();

  const [, forceUpdate] = useState({});
  const [isModalSendError, setIsModalSendError] = useState(false);
  const [isModalSendSuccess, setIsModalSendSuccess] = useState(false);

  useEffect(() => {
    forceUpdate({});
  }, []);

  const onFinishHandler = () => {
    const value = form.getFieldValue("phone");
    const password = form.getFieldValue("password");

    const phone = phoneValid.test(value) && value;
    const email = emailValid.test(value) && value;

    dispatch(_login({ phone, email, password }));
  };

  const hiddenEmail = useMemo(() => {
    const value = form.getFieldValue("phone");

    if (emailValid.test(value)) {
      return maskEmail(value);
    }
  }, [form]);

  const onPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone = addPlusToNumber(e.target.value);

    form.setFieldsValue({ phone });
  };

  const onForgotPassword = () => {
    navigate("/forgotpassword");
  };

  return (
    <Row justify={"center"} align={"middle"}>
      <Col span={24}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinishHandler}
          className={styles.formBlock}
        >
          <Form.Item
            name="phone"
            className={styles.formBlock}
            rules={[
              {
                required: true,
                message: "Введите номер телефона или email",
                min: 4,
              },
              {
                validator: (_, value) => {
                  if (
                    value &&
                    !phoneValid.test(value) &&
                    !emailPattern.test(value)
                  ) {
                    return Promise.reject(
                      "Неправильный формат номера или почты"
                    );
                  }

                  if (
                    value &&
                    !emailValid.test(value) &&
                    !phoneValid.test(value)
                  ) {
                    return Promise.reject("Неправильный формат почты");
                  }

                  return Promise.resolve();
                },
              },
            ]}
            hasFeedback
          >
            <Input
              className={styles.inpGroup}
              placeholder="Телефон или email"
              onChange={onPhoneChange}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: "Пожалуйста, введите ваш пароль" },
            ]}
            hasFeedback
          >
            <Input.Password
              allowClear
              placeholder="*********"
              onPressEnter={onFinishHandler}
              className={styles.inpGroup}
            />
          </Form.Item>

          <Col className={styles.forgotPassword}>
            <Button
              className={styles.forgotPasswordLink}
              size="large"
              type="link"
              onClick={onForgotPassword}
            >
              Забыли пароль?
            </Button>
          </Col>

          <Form.Item shouldUpdate>
            {() => {
              const phone = form.getFieldValue("phone");
              const isValid =
                (!!phone && phoneValid.test(phone)) || emailValid.test(phone);

              return (
                <Button
                  type="primary"
                  htmlType="submit"
                  className={styles.buttonLogin}
                  loading={_load_button}
                  disabled={!isValid}
                  block
                >
                  Войти
                </Button>
              );
            }}
          </Form.Item>
        </Form>
      </Col>
      <div className={styles.errors}>
        {_errors[0] && <Text type="danger">{_errors}</Text>}
      </div>

      <Modal
        className={styles.modalSendMessageSuccess}
        open={isModalSendSuccess}
        closable={false}
        footer={false}
        onCancel={() => setIsModalSendSuccess(false)}
      >
        <Icon component={CoolIcon} />
        <h2>Письмо отправлено на {hiddenEmail}</h2>
      </Modal>

      <Modal
        className={styles.modalForgotPassword}
        open={isModalSendError}
        closable={false}
        width={520}
        onCancel={() => setIsModalSendError(false)}
        footer={[
          <Button
            className={`${styles.buttonLogin} ${styles.btnWidth}`}
            type="primary"
            key="submit"
            loading={_load_button}
            onClick={() => setIsModalSendError(false)}
          >
            Войти в систему
          </Button>,
        ]}
      >
        <Icon component={DoneIcon} className={styles.emptyIcon} />
        <h2 className={styles.emptyModalTitle}>Отсутствует e-mail</h2>
        <p className={styles.emptyModalText}>
          К сожалению, вы не указывали электронную почту, воспользуйтесь входом
          через одноразовый код
        </p>
      </Modal>
    </Row>
  );
};

export default Anytime;
